import { useEffect, useState } from 'react';
import type { LiveAlert } from '../../lib/liveFeed.js';
import { fmtAge } from '../../lib/fmt.js';
import { IconClose } from '../Icons.js';

const SHOW_MS = 14_000;

/**
 * Live alert stack over the map (whales, linked groups dumping or loading
 * up, creator selling, exchange deposits). Newest on top, each stays ~14 s
 * (hover keeps it), click opens the wallet / group. The bell keeps the
 * recent ones for anyone who looked away.
 */
export function LiveAlerts({ alerts, onOpen, onDismiss, compact }: { alerts: LiveAlert[]; onOpen: (a: LiveAlert) => void; onDismiss: (id: string) => void; compact?: boolean }) {
  const [now, setNow] = useState(Date.now());
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // hovering keeps the toasts up a while longer
  const fresh = alerts.filter((a) => now - a.at < (hover ? SHOW_MS * 4 : SHOW_MS));
  const shown = fresh.slice(0, compact ? 1 : 3);
  if (!alerts.length) return null;
  return (
    <div className={`la ${compact ? 'compact' : ''}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} aria-live="polite">
      <button className={`la-bell ${open ? 'on' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} title="Recent live alerts">
        <i className="la-live" /> Live alerts <b>{alerts.length}</b>
      </button>
      {open ? (
        <div className="la-list" role="list">
          {alerts.map((a) => (
            <Item key={a.id} a={a} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        shown.map((a) => <Item key={a.id} a={a} onOpen={onOpen} onDismiss={onDismiss} toast />)
      )}
    </div>
  );
}

function Item({ a, onOpen, onDismiss, toast }: { a: LiveAlert; onOpen: (a: LiveAlert) => void; onDismiss?: (id: string) => void; toast?: boolean }) {
  return (
    <div className={`la-item ${a.tone} ${toast ? 'la-pop' : ''}`} role="listitem">
      <button className="la-main" onClick={() => onOpen(a)}>
        <i className="la-dot" />
        <span>
          <b>{a.title}</b>
          <em>
            {a.detail} · {fmtAge(a.ts)} ago
          </em>
        </span>
      </button>
      {onDismiss && (
        <button className="la-x" onClick={() => onDismiss(a.id)} aria-label="Dismiss">
          <IconClose size={13} />
        </button>
      )}
    </div>
  );
}
